import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type {
  Word,
  Story,
  GameState,
  RecordingState,
  PronunciationFeedback,
  GameSession,
  AudioAnalysisResult
} from '../types'
import { supabase } from '../services/supabase'

interface GameStoreState {
  // Game state
  gameState: GameState
  recordingState: RecordingState
  feedback: PronunciationFeedback | null
  history: GameSession[]

  // Loading states
  loading: boolean
  error: string | null

  // Current session
  currentSession: GameSession | null

  // Audio playback
  recordedAudioBlob: Blob | null
}

interface GameStoreActions {
  // Game actions
  startGame: (mode: GameState['gameMode'], difficulty: GameState['difficulty'], word?: Word) => void
  endGame: () => Promise<void>
  pauseGame: () => void
  resumeGame: () => void
  setCurrentWord: (word: Word) => void
  setCurrentStory: (story: Story) => void
  updateScore: (points: number) => void
  updateStreak: (correct: boolean) => void
  nextWord: () => Promise<void>
  nextPanel: () => void
  previousPanel: () => void
  setCurrentPanel: (panelIndex: number) => void

  // Recording actions
  startRecording: () => Promise<void>
  stopRecording: () => Promise<Blob | null>
  processAudio: (audioBlob: Blob, targetWord?: string) => Promise<AudioAnalysisResult | null>
  clearRecording: () => void
  playRecordedAudio: () => void

  // Feedback actions
  setFeedback: (feedback: PronunciationFeedback) => void
  clearFeedback: () => void

  // Session management
  createSession: () => Promise<void>
  updateSession: (updates: Partial<GameSession>) => Promise<void>
  loadHistory: () => Promise<void>

  // Utility actions
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  reset: () => void
}

type GameStore = GameStoreState & GameStoreActions

const initialGameState: GameState = {
  score: 0,
  streak: 0,
  gameMode: 'word_practice',
  difficulty: 3,
  isPlaying: false,
  isPaused: false,
  currentPanel: 0,
  gameEnded: false
}

const initialRecordingState: RecordingState = {
  isRecording: false,
  isProcessing: false,
  duration: 0
}

let mediaRecorder: MediaRecorder | null = null
let audioChunks: Blob[] = []
let recordingTimer: NodeJS.Timeout | null = null

export const useGameStore = create<GameStore>()(
  subscribeWithSelector(
    (set, get) => ({
      // Initial state
      gameState: initialGameState,
      recordingState: initialRecordingState,
      feedback: null,
      history: [],
      loading: false,
      error: null,
      currentSession: null,
      recordedAudioBlob: null,

      // Game actions
      startGame: (mode, difficulty, word) => {
        set({
          gameState: {
            ...initialGameState,
            gameMode: mode,
            difficulty,
            isPlaying: true,
            gameEnded: false,
            currentWord: word
          },
          feedback: null,
          error: null
        })
        get().createSession()
      },

      endGame: async () => {
        const { currentSession } = get()
        if (currentSession) {
          await get().updateSession({
            duration_seconds: currentSession.started_at ? Math.floor((Date.now() - new Date(currentSession.started_at).getTime()) / 1000) : 0,
            ended_at: new Date().toISOString()
          })
        }

        set({
          gameState: { ...get().gameState, isPlaying: false, gameEnded: true },
          currentSession: null
        })
      },

      pauseGame: () => {
        set({
          gameState: { ...get().gameState, isPaused: true }
        })
      },

      resumeGame: () => {
        set({
          gameState: { ...get().gameState, isPaused: false }
        })
      },

      setCurrentWord: (word) => {
        set({
          gameState: { ...get().gameState, currentWord: word }
        })
      },

      setCurrentStory: (story) => {
        set({
          gameState: {
            ...get().gameState,
            currentStory: story,
            currentPanel: 0
          }
        })
      },

      updateScore: (points) => {
        const currentScore = get().gameState.score
        set({
          gameState: { ...get().gameState, score: currentScore + points }
        })
      },

      updateStreak: (correct) => {
        const currentStreak = get().gameState.streak
        set({
          gameState: {
            ...get().gameState,
            streak: correct ? currentStreak + 1 : 0
          }
        })
      },

      nextWord: async () => {
        const { gameState } = get()
        try {
          const { data: words, error } = await supabase
            .from('words')
            .select('*')
            .eq('difficulty_level', gameState.difficulty)
            .limit(50)

          if (error) throw error

          if (words && words.length > 0) {
            const randomWord = words[Math.floor(Math.random() * words.length)]
            set({
              gameState: { ...gameState, currentWord: randomWord },
              feedback: null
            })
          }
        } catch (error) {
          console.error('Error fetching next word:', error)
          get().setError('Failed to load next word')
        }
      },

      nextPanel: () => {
        const { gameState } = get()
        const maxPanels = gameState.currentStory?.comic_panels.length || 0
        if (gameState.currentPanel !== undefined && gameState.currentPanel < maxPanels - 1) {
          set({
            gameState: {
              ...gameState,
              currentPanel: gameState.currentPanel + 1
            }
          })
        }
      },

      previousPanel: () => {
        const { gameState } = get()
        if (gameState.currentPanel !== undefined && gameState.currentPanel > 0) {
          set({
            gameState: {
              ...gameState,
              currentPanel: gameState.currentPanel - 1
            }
          })
        }
      },

      setCurrentPanel: (panelIndex: number) => {
        const { gameState } = get()
        const maxPanels = gameState.currentStory?.comic_panels.length || 0
        if (panelIndex >= 0 && panelIndex < maxPanels) {
          set({
            gameState: {
              ...gameState,
              currentPanel: panelIndex
            }
          })
        }
      },

      // Recording actions
      startRecording: async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

          audioChunks = []
          mediaRecorder = new MediaRecorder(stream)

          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              audioChunks.push(event.data)
            }
          }

          mediaRecorder.start()

          // Start timer
          let duration = 0
          recordingTimer = setInterval(() => {
            duration += 100
            set({
              recordingState: { ...get().recordingState, duration }
            })
          }, 100)

          set({
            recordingState: {
              ...get().recordingState,
              isRecording: true,
              duration: 0,
              error: undefined
            }
          })
        } catch (error) {
          console.error('Error starting recording:', error)
          set({
            recordingState: {
              ...get().recordingState,
              error: 'Failed to access microphone'
            }
          })
        }
      },

      stopRecording: async () => {
        return new Promise((resolve) => {
          if (!mediaRecorder || mediaRecorder.state === 'inactive') {
            resolve(null)
            return
          }

          mediaRecorder.onstop = () => {
            const audioBlob = new Blob(audioChunks, { type: 'audio/wav' })
            const audioUrl = URL.createObjectURL(audioBlob)

            set({
              recordingState: {
                ...get().recordingState,
                isRecording: false,
                audioBlob,
                audioUrl
              },
              recordedAudioBlob: audioBlob
            })

            // Stop all tracks
            mediaRecorder?.stream.getTracks().forEach(track => track.stop())

            if (recordingTimer) {
              clearInterval(recordingTimer)
              recordingTimer = null
            }

            resolve(audioBlob)
          }

          mediaRecorder.stop()
        })
      },

      processAudio: async (audioBlob, targetWord) => {
        try {
          set({
            recordingState: { ...get().recordingState, isProcessing: true },
            error: null
          });

          // Check if user is authenticated
          const { data: { user }, error: authError } = await supabase.auth.getUser();
          if (authError || !user) {
            throw new Error('User must be authenticated to upload audio');
          }

          // Create FormData for the Gemini-based function
          const fileName = `audio_${Date.now()}.wav`;
          const formData = new FormData();
          formData.append('audio', audioBlob, fileName);
          formData.append('word', targetWord || '');
          formData.append('userId', user.id);
          formData.append('wordId', get().gameState.currentWord?.id || '');

          // Call speech analysis Gemini Edge Function
          const { data, error } = await supabase.functions.invoke('speech-analysis-gemini', {
            body: formData
          });

          if (error) throw error;

          const result: AudioAnalysisResult = data;

          // Convert to PronunciationFeedback format
          const overallScore = typeof result.pronunciation_score === 'number'
            ? result.pronunciation_score
            : result.pronunciation_score.overall;

          const feedback: PronunciationFeedback = {
            overall_score: overallScore,
            accuracy: result.word_accuracy || overallScore,
            fluency: result.fluency_score || overallScore,
            completeness: overallScore,
            feedback_text: result.feedback,
            suggestions: result.feedback.split('. ').filter(s => s.length > 0),
            phoneme_breakdown: result.phoneme_scores
          };

          set({ feedback });

          // Update game score based on pronunciation score
          const points = Math.round(overallScore * 10);
          get().updateScore(points);
          get().updateStreak(overallScore >= 70);

          return result;
        } catch (error) {
          console.error('Error processing audio:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to process audio';
          set({ error: errorMessage });
          return null;
        } finally {
          set({
            recordingState: { ...get().recordingState, isProcessing: false }
          });
        }
      },

      clearRecording: () => {
        const { recordingState } = get()
        if (recordingState.audioUrl) {
          URL.revokeObjectURL(recordingState.audioUrl)
        }

        set({
          recordingState: {
            ...initialRecordingState
          },
          recordedAudioBlob: null
        })
      },

      // Feedback actions
      setFeedback: (feedback) => set({ feedback }),
      clearFeedback: () => set({ feedback: null }),

      playRecordedAudio: () => {
        const { recordedAudioBlob } = get()
        if (recordedAudioBlob) {
          const audioUrl = URL.createObjectURL(recordedAudioBlob)
          const audio = new Audio(audioUrl)
          audio.play().catch(error => {
            console.error('Error playing audio:', error)
          })
          audio.onended = () => {
            URL.revokeObjectURL(audioUrl)
          }
        }
      },

      // Session management
      createSession: async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) return

          const { gameState } = get()
          const { data: session, error } = await supabase
            .from('game_sessions')
            .insert({
              user_id: user.id,
              session_type: gameState.gameMode,
              total_score: gameState.score
            })
            .select()
            .single()

          if (error) throw error
          set({ currentSession: session as GameSession })
        } catch (error) {
          console.error('Error creating session:', error)
        }
      },

      updateSession: async (updates) => {
        try {
          const { currentSession } = get()
          if (!currentSession) return

          const { data: updatedSession, error } = await supabase
            .from('game_sessions')
            .update({
              ...updates,
              total_score: get().gameState.score,
              updated_at: new Date().toISOString()
            })
            .eq('id', currentSession.id)
            .select()
            .single()

          if (error) throw error
          set({ currentSession: updatedSession as GameSession })
        } catch (error) {
          console.error('Error updating session:', error)
        }
      },

      loadHistory: async () => {
        try {
          set({ loading: true })
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) return

          const { data: history, error } = await supabase
            .from('game_sessions')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(50)

          if (error) throw error
          set({ history: (history || []) as GameSession[] })
        } catch (error) {
          console.error('Error loading history:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to load history' })
        } finally {
          set({ loading: false })
        }
      },

      // Utility actions
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),

      reset: () => {
        get().clearRecording()
        set({
          gameState: initialGameState,
          recordingState: initialRecordingState,
          feedback: null,
          currentSession: null,
          error: null,
          recordedAudioBlob: null
        })
      }
    })
  )
)

// Selectors
export const useGameState = () => useGameStore((state) => state.gameState)
export const useRecordingState = () => useGameStore((state) => state.recordingState)
export const useFeedback = () => useGameStore((state) => state.feedback)
export const useGameHistory = () => useGameStore((state) => state.history)
export const useGameLoading = () => useGameStore((state) => state.loading)
export const useGameError = () => useGameStore((state) => state.error)

// Helper hooks
export const useIsPlaying = () => {
  const gameState = useGameState()
  return gameState.isPlaying && !gameState.isPaused
}

export const useCanRecord = () => {
  const gameState = useGameState()
  const recordingState = useRecordingState()
  return gameState.isPlaying && !recordingState.isRecording && !recordingState.isProcessing
}

export const useCurrentWord = () => {
  const gameState = useGameState()
  return gameState.currentWord
}

export const useCurrentStory = () => {
  const gameState = useGameState()
  return gameState.currentStory
}